/// <reference lib="webworker" />
// export default null;
declare let self: ServiceWorkerGlobalScope;

// Declare the injected variables from Vite
declare const __VERSION_HASH__: string;
declare const __PACKAGE_VERSION__: string;
declare const __SW_VERSION__: string;

// Use fallbacks for undefined variables
const VERSION_HASH =
	typeof __VERSION_HASH__ !== 'undefined' ? __VERSION_HASH__ : 'dev-hash';
const PACKAGE_VERSION =
	typeof __PACKAGE_VERSION__ !== 'undefined'
		? __PACKAGE_VERSION__
		: '0.0.0-dev';
const SW_VERSION =
	typeof __SW_VERSION__ !== 'undefined'
		? __SW_VERSION__
		: `${PACKAGE_VERSION}-${VERSION_HASH}`;

import {
	precacheAndRoute,
	cleanupOutdatedCaches,
	createHandlerBoundToURL,
} from 'workbox-precaching';
import { clientsClaim } from 'workbox-core';

import { registerRoute, Route, NavigationRoute } from 'workbox-routing';
import { NetworkFirst, CacheFirst, NetworkOnly } from 'workbox-strategies';
import { ExpirationPlugin } from 'workbox-expiration';
import { Queue } from 'workbox-background-sync';
import { set, del, entries, createStore } from 'idb-keyval';
import { reportSwDownloadFailure } from '@shared/observability/reporters/sw-reporter';
// Pure helpers, extracted so they're testable -- see that module's comment.
import {
	buildEvictionQueue,
	type AssetOwnerRecord,
} from '@shared/sw/eviction-queue';
import { Logger } from '@shared/lib/logger';

// Same Logger used on the page -- collapses this file's former hand-rolled
// log/info/error/logStyle helpers (a third copy of the same
// colored-console-badge pattern, with a bug the shared class doesn't have:
// `error()` used to call `console.info`, so SW error-level output never
// actually reached `console.error`). No browser-only APIs in Logger, so
// it's safe inside a service worker's global scope.
// Declared before first use -- `swVersion()` calls `info()` at module top
// level (below), which runs before this file finishes evaluating top to
// bottom, so the instance must exist by then, not further down the file
// where the old helpers used to live.
const swLogger = new Logger('sw', {
	log: '#64d1ff',
	info: '#fed709',
	error: '#ff375f',
});

function log(msg: unknown, ...optionalParams: unknown[]) {
	swLogger.log(msg, ...optionalParams);
}

function info(msg: unknown, ...optionalParams: unknown[]) {
	swLogger.info(msg, ...optionalParams);
}

function error(msg: unknown, ...optionalParams: unknown[]) {
	swLogger.error(msg, ...optionalParams);
}

// Precache the app shell (JS/CSS/HTML/icons) so the installed app opens with no network.
precacheAndRoute(self.__WB_MANIFEST);
cleanupOutdatedCaches();

// The version-file check (see App-level useBuildVersionCheck) always needs the live
// file, never a cached copy, regardless of what any other route here might match.
registerRoute(
	({ url }) => url.pathname === '/build' || url.pathname.endsWith('/build'),
	new NetworkOnly(),
);

// Artificial delay before starting *low-priority* background prefetch (data-
// driven covers/thumbnails, not necessarily on screen), so it doesn't
// contend with bandwidth the current page load still needs. High-priority
// downloads (images already rendered on screen — see DownloadPriority below)
// skip this: the content is already visible, so there's no "initial load"
// left to contend with, and the whole point of prioritizing them is defeated
// if they sit behind the same delay as everything else.
const DELAY = 5 * 1000;

info('Service Worker loading with version:', SW_VERSION);
swVersion();

const CACHE_PREFIX = 'cf';
const CACHE_IMAGES_NAME = `${CACHE_PREFIX}-images`;
const CACHE_AUDIOS_NAME = `${CACHE_PREFIX}-audios`;
const CACHE_VIDEOS_NAME = `${CACHE_PREFIX}-videos`;
const CACHE_FONTS_NAME = `${CACHE_PREFIX}-fonts`;
const CACHE_ASSETS_NAME = `${CACHE_PREFIX}-assets`;
const VERSION_CACHE_NAME = `${CACHE_PREFIX}-version`;

// Per-issue cache identity (improvements/DASHBOARD.md, Section 02, "New
// (2026-08-19) -- per-issue cache identity"). The media caches above
// (images/audios/videos) are bucketed by asset *type* only -- a URL cached
// today carries no record of which issue it belongs to, so "evict the
// least-recently-read issue" or "delete this un-favorited issue's
// downloads" have nothing to act on; only a total cache wipe is possible.
// This is a separate IndexedDB database (idb-keyval, same library the app
// side already uses for the query persister) mapping
// `url -> { issueId, cacheName }` for every asset downloaded with a known
// issue owner. It's additive, read alongside the Cache Storage entries it
// describes -- deleting/evicting through it always deletes from both the
// real cache and this index together (see evictIssue below), so the two
// never have a chance to drift apart in normal operation. Fonts and shared
// CSS assets are deliberately never recorded here: they're tenant-wide, not
// owned by any one issue, and stay behind the un-touched
// purgeOnQuotaError/maxAge policy on their own caches.
const issueIndexStore = createStore('cf-issue-index', 'urls');

async function recordAssetOwner(
	url: string,
	issueId: string,
	cacheName: string,
): Promise<void> {
	try {
		await set(url, { issueId, cacheName, savedAt: Date.now() }, issueIndexStore);
	} catch (e) {
		// Best-effort: losing an index entry only degrades eviction
		// precision for this one URL, never breaks the download itself.
		error('Failed to record cache ownership for', url, e);
	}
}

// Deletes every cached asset attributed to one issue, across whichever
// media caches it actually has entries in, plus the index entries
// themselves. No secondary index by issueId is kept -- a full scan of the
// (url -> owner) store is simplest and, at the scale one tenant's install
// actually reaches (hundreds to low thousands of cached URLs, not
// millions), a measured-enough cost not worth a second IndexedDB store and
// the drift-between-two-stores risk that comes with it.
async function evictIssue(issueId: string): Promise<void> {
	const all = await entries<string, AssetOwnerRecord>(issueIndexStore);
	const owned = all.filter(([, record]) => record.issueId === issueId);

	let deleted = 0;
	for (const [url, record] of owned) {
		try {
			const cache = await caches.open(record.cacheName);
			if (await cache.delete(url)) {
				deleted += 1;
			}
		} catch (e) {
			error(`Failed to evict ${url} for issue ${issueId}:`, e);
		} finally {
			await del(url, issueIndexStore).catch(() => undefined);
		}
	}

	info(
		`Evicted ${deleted}/${owned.length} cached assets for issue ${issueId}`,
	);
}

/**
 * Fraction of the origin's storage quota above which we start reclaiming
 * space by evicting whole issues.
 *
 * 0.85 rather than something closer to 1.0 on purpose: browsers do not hand
 * out the last slice of quota gracefully -- writes start failing *before*
 * `usage` reaches `quota`, and Safari in particular reports a quota that is
 * more of an aspiration than a guarantee. Leaving ~15% headroom means
 * reclamation happens while writes still succeed, rather than as a reaction
 * to the failure it exists to prevent.
 */
const STORAGE_PRESSURE_THRESHOLD = 0.85;

/**
 * Byte-driven eviction (improvements/DASHBOARD.md Section 02, "wire
 * `useStorageEstimate()` to the cache layer", resolved 2026-08-28).
 *
 * The app-side `useStorageEstimate()` hook surfaces usage/quota for a future
 * settings UI (topic 20 owns that screen). The *signal*, though, belongs
 * here: `navigator.storage.estimate()` is available in a service-worker
 * scope, and the SW is the only context that both knows when new bytes just
 * landed and can act on it without a page being open.
 *
 * Policy: when usage crosses `STORAGE_PRESSURE_THRESHOLD`, evict whole issues
 * -- oldest-downloaded first -- until back under it or nothing is left to
 * evict. Whole issues rather than individual URLs because a half-evicted
 * issue is the worst outcome available: it still occupies space, and it
 * renders as a broken issue with missing images rather than as one the reader
 * can knowingly re-download. `evictIssue()` already deletes from both the
 * Cache Storage entry and the ownership index together, so the two can't
 * drift.
 *
 * "Oldest downloaded" is a deliberate approximation of LRU. True LRU needs a
 * read timestamp updated on every cache hit, which means an IndexedDB write
 * on the hot path of every image load -- a real cost for a policy that only
 * runs under pressure. First-downloaded is cheap, monotonic, and in a reader
 * app correlates well with "least likely to be opened again".
 *
 * Best-effort throughout: any failure logs and returns rather than throwing
 * into the caller's `waitUntil`, since failing to reclaim space must never
 * fail the download that triggered the check.
 */
async function relieveStoragePressure(): Promise<void> {
	if (!('storage' in navigator) || !('estimate' in navigator.storage)) {
		return;
	}

	try {
		const measure = async () => {
			const { usage, quota } = await navigator.storage.estimate();
			if (!usage || !quota) {
				return undefined;
			}
			return usage / quota;
		};

		let ratio = await measure();
		if (ratio === undefined || ratio < STORAGE_PRESSURE_THRESHOLD) {
			return;
		}

		info(
			`Storage at ${(ratio * 100).toFixed(1)}% of quota -- reclaiming space`,
		);

		// Oldest-first queue of issue ids, built from the ownership index.
		// Ordering lives in `@shared/sw/eviction-queue` so it can be tested --
		// this module can't be imported into a test (top-level listener and
		// route registration).
		const all = await entries<string, AssetOwnerRecord>(issueIndexStore);
		const queue = buildEvictionQueue(all);

		for (const issueId of queue) {
			await evictIssue(issueId);
			ratio = await measure();
			if (ratio === undefined || ratio < STORAGE_PRESSURE_THRESHOLD) {
				info(`Storage pressure relieved after evicting ${issueId}`);
				return;
			}
		}

		// Everything attributable to an issue is gone and we're still over.
		// Nothing further to do from here: what remains is the precached app
		// shell and the tenant-wide font/asset caches, which carry their own
		// `purgeOnQuotaError` backstop.
		error(
			`Storage still at ${
				ratio === undefined ? 'unknown' : `${(ratio * 100).toFixed(1)}%`
			} after evicting every indexed issue`,
		);
	} catch (e) {
		error('Failed to relieve storage pressure:', e);
	}
}

// Retries downloads interrupted by connectivity loss: replayed via the
// Background Sync API where supported, and on the SW's `online` event /
// next startup as a fallback where it isn't (Safari).
const downloadRetryQueue = new Queue('cf-download-retry-queue', {
	maxRetentionTime: 24 * 60, // minutes
	onSync: async ({ queue }) => {
		let entry = await queue.shiftRequest();
		while (entry) {
			const cacheDestination = (
				entry.metadata as { cacheName?: string } | undefined
			)?.cacheName;
			try {
				const response = await fetch(entry.request.clone());
				if (!response.ok) {
					// Permanent failure (404, 500, ...) on replay too — drop
					// it instead of requeuing forever.
					error(
						`Retry gave up on ${entry.request.url}: HTTP ${response.status}`,
					);
					if (cacheDestination) {
						void reportSwDownloadFailure({
							cacheName: cacheDestination,
						});
					}
				} else if (cacheDestination) {
					const cache = await caches.open(cacheDestination);
					await cache.put(entry.request, response);
				}
			} catch (e) {
				await queue.unshiftRequest(entry);
				throw e;
			}
			entry = await queue.shiftRequest();
		}
	},
});

const THIRTY_DAYS_SECONDS = 30 * 24 * 60 * 60;

/**
 * Cache expiration policy (improvements/DASHBOARD.md Section 02, "eviction
 * policy to replace `purgeOnQuotaError: true`", resolved 2026-08-28).
 *
 * Two things were wrong with the previous uniform
 * `{ purgeOnQuotaError: true, maxAgeSeconds: 30d }` on all five caches:
 *
 * 1. **No `maxEntries`.** Every cache grew unbounded until the device hit its
 *    quota. Nothing shed load gradually, so quota exhaustion was the *first*
 *    signal anything was wrong.
 * 2. **`purgeOnQuotaError` on the caches holding deliberate downloads.** That
 *    flag makes Workbox delete the **entire cache** on a single
 *    `QuotaExceededError`. For images/audio/video that means the first reader
 *    to fill their quota silently loses every downloaded issue at once --
 *    catastrophic, and precisely the opposite of the product's "it's right
 *    there offline" promise.
 *
 * The policy now splits by *what the cache holds*:
 *
 * - **Deliberate downloads** (images, audio, video): `purgeOnQuotaError:
 *   false` + a bounded `maxEntries`. Workbox LRU-evicts the oldest entries as
 *   the bound is approached, so a quota error costs one failed write (one
 *   missing image) instead of every issue the reader saved. Whole-issue
 *   reclamation under real pressure is handled deliberately by
 *   `relieveStoragePressure()` below, which knows about issue ownership --
 *   Workbox's per-cache LRU does not.
 * - **Disposable, tenant-wide** (fonts, shared assets): `purgeOnQuotaError:
 *   true` kept. These are small, re-fetchable on next online load, owned by no
 *   issue, and a full purge costs a reader nothing but one refetch.
 *
 * `maxEntries` values are deliberate *bounds*, not tuned numbers -- there is
 * no per-tenant asset-count telemetry to tune against yet. They are set about
 * an order of magnitude above a realistic worst case (a 40-article issue runs
 * to a few hundred images; ten such issues kept offline is ~3-5k) so that in
 * normal use the bound never binds and `relieveStoragePressure()` is what
 * actually reclaims space. Entry counts are also a poor proxy for bytes --
 * one video outweighs a thousand thumbnails -- which is the other reason the
 * real policy is byte-driven and lives below.
 */
const imagesExpirationPlugin = new ExpirationPlugin({
	purgeOnQuotaError: false,
	maxEntries: 6000,
	maxAgeSeconds: THIRTY_DAYS_SECONDS,
});

const audiosExpirationPlugin = new ExpirationPlugin({
	purgeOnQuotaError: false,
	maxEntries: 300,
	maxAgeSeconds: THIRTY_DAYS_SECONDS,
});

const videosExpirationPlugin = new ExpirationPlugin({
	purgeOnQuotaError: false,
	maxEntries: 150,
	maxAgeSeconds: THIRTY_DAYS_SECONDS,
});

const fontsExpirationPlugin = new ExpirationPlugin({
	purgeOnQuotaError: true,
	maxEntries: 100,
	maxAgeSeconds: THIRTY_DAYS_SECONDS,
});

const assetsExpirationPlugin = new ExpirationPlugin({
	purgeOnQuotaError: true,
	maxEntries: 200,
	maxAgeSeconds: THIRTY_DAYS_SECONDS,
});

const cacheName = 'canvasflow'; //cacheNames.runtime;

const registerCacheFirstRouteUsing = (
	destination: RequestDestination,
	cacheName: string,
	expirationPlugin: ExpirationPlugin,
): Route =>
	registerRoute(
		({ request }) => request.destination === destination,
		new CacheFirst({
			cacheName: cacheName,
			plugins: [expirationPlugin],
		}),
	);

const registerCacheFirstRouteUsingExtension = (
	extensions: Array<string>,
	cacheName: string,
	expirationPlugin: ExpirationPlugin,
): Route =>
	registerRoute(
		({ request }) => {
			extensions.join('|');
			const reg = new RegExp(`.*\\.(?:${extensions.join('|')})$`);
			return reg.test(request.url);
		},
		new CacheFirst({
			cacheName: cacheName,
			plugins: [expirationPlugin],
		}),
	);

const registerWebAssetsImagesCacheFirstRouteUsing = (
	cacheName: string,
	expirationPlugin: ExpirationPlugin,
): Route =>
	registerRoute(
		(data) => {
			const { request, url } = data;
			if (
				url.origin.startsWith('https://www.google') ||
				url.origin.startsWith('https://analytics.google')
			) {
				return false;
			}
			// request.destination === 'image' is the spec-correct signal (set
			// by the browser for every <img>/srcset/picture load regardless
			// of URL shape or CORS mode) and is what actually makes this
			// route fire reliably on iOS Safari — the URL-extension regex
			// alone misses any CDN that serves images via extensionless or
			// query-string-based URLs (resize/format params, no trailing
			// .jpg/.png), which is common and isn't an iOS-specific gap, just
			// one this regex-only check silently failed on everywhere.
			return (
				request.destination === 'image' ||
				/.*\.(?:png|webp|jpg|jpeg|gif)$/.test(request.url)
			);
		},
		new CacheFirst({
			cacheName: cacheName,
			plugins: [expirationPlugin],
		}),
	);

registerWebAssetsImagesCacheFirstRouteUsing(
	CACHE_IMAGES_NAME,
	imagesExpirationPlugin,
);
// Previously skipped on Safari entirely, leaving Safari/iOS installs with no
// offline SPA-navigation fallback at all (a request for any inner route,
// e.g. /issue/42, would just fail offline instead of serving the cached
// shell). That exclusion predates app-shell precaching; with the shell now
// precached, register it unconditionally.
//
// networkTimeoutSeconds matters here beyond hard-offline: without it,
// NetworkFirst waits for the network attempt to definitively fail before
// falling back to the precached shell -- fine in airplane mode (fails
// instantly), but on a slow/flaky connection (weak signal, subway, elevator
// -- more common in practice than literal offline) it can hang far longer
// than a user will wait before ever reaching the cache fallback that's
// sitting right there. 4s caps that wait without meaningfully hurting the
// common case (a healthy connection resolves well under that).
//
// The `NetworkFirst` alone was not the SPA-navigation fallback the comment
// above describes -- it only looks the *exact requested URL* up in
// `cacheName`, and that cache is only ever populated by a real, full-document
// browser navigation (a hard reload, a typed URL, a bookmark). Once the app
// has booted, every in-app route change is handled client-side by TanStack
// Router, which never issues one of those -- so `cacheName` ends up holding
// only whichever single URL the user's very first real page load happened to
// be (almost always `/`). Cold-opening any other route while offline --
// exactly the PWA scenario of relaunching from the home-screen icon on a
// bookmarked or last-open deep link -- had no entry to fall back to, so the
// request fell through to the network, which was also down, and the browser
// showed its own connection-error page instead of the app. Confirmed live:
// reproduced identically on morningstar and poets, since this file has no
// per-template branching -- every tenant on every template hit it the same
// way.
//
// `navigationFallback`, using `createHandlerBoundToURL`, is workbox's own
// documented recipe for exactly this: it serves the *precached* app shell
// (`index.html`, always present -- `precacheAndRoute` above guarantees it)
// regardless of which URL was requested. Composed as the explicit `catch`
// only after `NetworkFirst` itself has exhausted network-then-exact-cache, so
// online behaviour, the 4s timeout, and a same-URL cache hit (a repeat visit
// to `/`) are all unchanged -- this only fires in the one case that used to
// dead-end: no network, and no cache entry for that specific URL.
const navigationFallback = createHandlerBoundToURL('index.html');
const navigationHandler = new NetworkFirst({
	cacheName,
	networkTimeoutSeconds: 4,
});

registerRoute(
	new NavigationRoute(async (params) => {
		try {
			return await navigationHandler.handle(params);
		} catch {
			return navigationFallback(params);
		}
	}),
);

registerCacheFirstRouteUsingExtension(
	['m3u8', 'ts', 'mp3'],
	CACHE_AUDIOS_NAME,
	audiosExpirationPlugin,
);

registerCacheFirstRouteUsing(
	'audio',
	CACHE_AUDIOS_NAME,
	audiosExpirationPlugin,
);
registerCacheFirstRouteUsing(
	'track',
	CACHE_AUDIOS_NAME,
	audiosExpirationPlugin,
);
registerCacheFirstRouteUsing(
	'video',
	CACHE_VIDEOS_NAME,
	videosExpirationPlugin,
);

registerCacheFirstRouteUsing('font', CACHE_FONTS_NAME, fontsExpirationPlugin);

registerCacheFirstRouteUsing(
	'style',
	CACHE_ASSETS_NAME,
	assetsExpirationPlugin,
);

self.addEventListener('install', (event: ExtendableEvent) => {
	info(`SERVICE WORKER INSTALLATION: ${SW_VERSION}`);
	event.waitUntil(
		(async () => {
			// Best-effort priming of '/' — must never block skipWaiting. If this
			// rejects (transient network blip, redirect, etc.) the new worker
			// still needs to take over, otherwise it gets stuck in "waiting"
			// forever while the old worker keeps serving a previous deploy's
			// (now possibly-nonexistent) precached asset filenames.
			try {
				const cache = await caches.open(cacheName);
				await cache.addAll(['/']);
			} catch (e) {
				error(e);
			}
			await self.skipWaiting();
		})(),
	);
});

self.addEventListener('activate', (event: ExtendableEvent) => {
	info(`ACTIVATE SERVICE WORKER: ${SW_VERSION}`);
	event.waitUntil(Promise.all([self.clients.claim(), handleVersionUpdate()]));
});

self.addEventListener('message', (event: ExtendableMessageEvent) => {
	const { type, payload } = event.data;
	const messageType = new Set([
		'DOWNLOAD_IMAGES',
		'DOWNLOAD_BACKGROUND_IMAGES',
		'DOWNLOAD_BACKGROUND_REPLICA_PAGES',
		'DOWNLOAD_BACKGROUND_VIDEOS',
		'EVICT_ISSUE',
		'VERSION',
	]);

	if (!messageType.has(type)) {
		return;
	}

	info(`RECEIVE MESSAGE: "${type}"`);

	// Every handler below defers its real work behind a setTimeout (to avoid
	// contending with the initial page load's own bandwidth). Without
	// waitUntil(), the browser is free to terminate the service worker the
	// moment this synchronous handler returns — there's nothing telling it
	// work is still pending. On iOS especially, backgrounding/closing the
	// app kills the SW quickly, well inside that delay window, so the
	// download silently never happens even though the message was received.
	switch (type) {
		case 'DOWNLOAD_IMAGES':
			event.waitUntil(onDownloadImages(payload, event));
			break;
		case 'VERSION':
			swVersion();
			break;
		case 'DOWNLOAD_BACKGROUND_IMAGES':
			event.waitUntil(onDownloadBackgroundImage(payload));
			break;
		case 'DOWNLOAD_BACKGROUND_REPLICA_PAGES':
			event.waitUntil(onDownloadBackgroundPages(payload));
			break;
		case 'DOWNLOAD_BACKGROUND_VIDEOS':
			event.waitUntil(onDownloadVideos(payload));
			break;
		case 'EVICT_ISSUE':
			event.waitUntil(evictIssue(String(payload.IssueID)));
			break;
		default:
			return;
	}
});

function delay(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

type DownloadPriority = 'high' | 'low';

// Shapes of the `message` event payloads this worker handles -- postMessage
// contracts with App.tsx/sw.context.tsx and the per-issue prefetch hooks
// (use-issue.ts, per-template Home.hooks.ts), not derived from a shared
// type since the message itself crosses the main-thread/worker boundary
// with no static import connecting the two sides.
interface DownloadPagesPayload {
	IssueID?: string;
	pages: Array<string>;
}

interface DownloadImagesPayload {
	images: Array<string>;
	priority?: DownloadPriority;
	IssueID?: string;
	// Per-URL issue ownership for batches spanning more than one issue (a
	// catalogue page's covers) -- see ServiceWorkerMessage's comment on the
	// app side (shared/sw/sw.context.tsx) for the full reasoning.
	imageOwners?: Record<string, string>;
}

interface DownloadVideosPayload {
	videos: Array<string>;
	IssueID?: string;
}

// Resolves which issue (if any) owns a given URL in a download batch: a
// single IssueID covers the whole batch (an open issue's own article
// images, replica pages, videos); imageOwners covers a mixed batch where
// each URL can belong to a different issue (catalogue covers/thumbnails).
function ownerResolver(
	issueId: string | undefined,
	perUrlOwners?: Record<string, string>,
): (url: string) => string | undefined {
	if (issueId) return () => issueId;
	if (perUrlOwners) return (url: string) => perUrlOwners[url];
	return () => undefined;
}

const MAX_CONCURRENT_DOWNLOADS = 6;

// Every caller of downloadToCache — the DOM-visible-image scan
// (sw.context.tsx's MutationObserver) and every data-driven background
// prefetch (issue covers, article feeds, replica pages, videos) — shares
// this one scheduler instead of each call spinning up its own independent
// concurrency pool. Two problems that created otherwise:
//
// 1. Race/duplicate work: the same URL can legitimately be requested by two
//    different callers close together (most commonly: the generic DOM scan
//    sees an <img> the instant it renders, while a template's data hook
//    prefetches the same URL from the fetched issue/article payload). Two
//    independent downloadToCache() calls both running cache.match() before
//    either has cache.put() the result would both conclude "not cached yet"
//    and both fetch it. The Cache API itself is fine with that (put() on the
//    same key just overwrites — there's never more than one entry), but it's
//    a wasted duplicate network fetch, doubly wasteful on the cellular
//    connections this offline-support work exists for. inFlightDownloads
//    keyed by cache+url makes a second request for the same URL just await
//    the first one's result instead of starting a second fetch.
// 2. No prioritization: a large data-driven prefetch batch (e.g. every cover
//    in a 50-issue list) and a handful of genuinely-visible-right-now images
//    were competing for the same implicit bandwidth with no ordering. A
//    priority queue lets DOM-visible images (priority 'high') jump ahead of
//    background prefetch (priority 'low') without the prefetch batch being
//    starved outright — it still runs, just after the visible ones.
const inFlightDownloads = new Map<string, Promise<void>>();
const highPriorityQueue: Array<() => void> = [];
const lowPriorityQueue: Array<() => void> = [];
let activeDownloadWorkers = 0;

function pumpDownloadQueue(): void {
	while (activeDownloadWorkers < MAX_CONCURRENT_DOWNLOADS) {
		const next = highPriorityQueue.shift() ?? lowPriorityQueue.shift();
		if (!next) {
			return;
		}
		activeDownloadWorkers += 1;
		next();
	}
}

/**
 * Downloads a single URL into the given cache, deduplicating against any
 * identical in-flight request and respecting the shared priority queue.
 * Resolves once the URL is cached (or has permanently failed) — callers
 * don't need to know whether they triggered the fetch or piggybacked on
 * someone else's.
 */
function scheduleDownload(
	cacheDestination: string,
	url: string,
	priority: DownloadPriority,
	issueId?: string,
): Promise<void> {
	const key = `${cacheDestination}::${url}`;
	const existing = inFlightDownloads.get(key);
	if (existing) {
		return existing;
	}

	const promise = new Promise<void>((resolve) => {
		const run = async () => {
			try {
				const response = await fetch(url);
				if (!response.ok) {
					// Permanent failure (404, 500, ...) — retrying won't
					// help, so don't queue it, just skip it.
					error(`Failed to download ${url}: HTTP ${response.status}`);
					void reportSwDownloadFailure({
						cacheName: cacheDestination,
						issueId,
					});
					return;
				}
				const cache = await caches.open(cacheDestination);
				await cache.put(url, response);
				if (issueId) {
					await recordAssetOwner(url, issueId, cacheDestination);
				}
			} catch (e) {
				error(e);
				// Network-level failure (offline, connection drop): queue
				// the URL and let downloadRetryQueue replay it
				// automatically (Background Sync where supported, or on
				// the next online event otherwise).
				await downloadRetryQueue
					.pushRequest({
						request: new Request(url),
						metadata: { cacheName: cacheDestination },
					})
					.catch(error);
			} finally {
				inFlightDownloads.delete(key);
				activeDownloadWorkers -= 1;
				resolve();
				pumpDownloadQueue();
			}
		};
		(priority === 'high' ? highPriorityQueue : lowPriorityQueue).push(run);
	});

	inFlightDownloads.set(key, promise);
	pumpDownloadQueue();
	return promise;
}

/**
 * Single download pipeline shared by every "cache a batch of URLs" caller
 * (issue page images, background images, videos) — replaces four
 * near-identical copy-pasted functions that only differed by cache name and
 * a log message. Skips URLs already in the cache, routes the rest through
 * the shared priority queue above (so one broken URL doesn't abort the
 * whole batch, and identical URLs from concurrent callers share one fetch),
 * and optionally reports progress over the BroadcastChannel so a future
 * download-manager UI has real data to show instead of "downloading..."
 * with no percentage.
 */
async function downloadToCache(
	cacheDestination: string,
	urls: Array<string>,
	onProgress?: (completed: number, total: number, url: string) => void,
	priority: DownloadPriority = 'low',
	getOwner?: (url: string) => string | undefined,
): Promise<void> {
	const cache = await caches.open(cacheDestination);
	const urlsToDownload: Array<string> = [];
	for (const url of urls) {
		if (!(await cache.match(url))) {
			urlsToDownload.push(url);
		}
	}

	info(`Total sent to download: ${urls.length}`);
	info(`Downloading this amount: ${urlsToDownload.length}`);

	let completed = 0;
	await Promise.all(
		urlsToDownload.map(async (url) => {
			await scheduleDownload(
				cacheDestination,
				url,
				priority,
				getOwner?.(url),
			);
			completed += 1;
			onProgress?.(completed, urlsToDownload.length, url);
		}),
	);

	info(
		`Succeeded adding ${completed}/${urlsToDownload.length} to cache "${cacheDestination}"`,
	);

	// The one choke point every download path funnels through, and therefore
	// the right place to check whether those new bytes pushed the origin over
	// its quota budget. Skipped when nothing was actually written -- a
	// fully-cached re-request shouldn't cost an IndexedDB scan.
	if (completed > 0) {
		await relieveStoragePressure();
	}
}

async function onDownloadBackgroundPages(
	payload: DownloadPagesPayload,
): Promise<void> {
	const { IssueID } = payload;
	log(`Downloading pages in service worker from the replica: ${IssueID}`);
	await delay(DELAY);
	try {
		await downloadToCache(
			CACHE_IMAGES_NAME,
			payload.pages,
			undefined,
			undefined,
			ownerResolver(IssueID),
		);
		info(`Success downloading the pages`);
	} catch (e) {
		error(e);
	}
}

async function onDownloadBackgroundImage(
	payload: DownloadImagesPayload,
): Promise<void> {
	info(`Downloading images in service worker: ${payload.images.length}`);
	// Callers pass priority explicitly: the DOM-visible-image scan
	// (sw.context.tsx) sends 'high' since those images are on screen right
	// now; the data-driven prefetch hooks (use-issue.ts, per-template
	// Home.hooks.ts, etc.) don't set it and default to 'low' — they cache
	// covers/thumbnails regardless of current viewport, so they shouldn't
	// compete evenly with what's actually visible.
	const priority: DownloadPriority =
		payload.priority === 'high' ? 'high' : 'low';
	// High-priority requests skip the bandwidth-contention delay entirely —
	// see DELAY's definition for why. Otherwise a genuinely on-screen image
	// would sit behind the same 5s wait as an off-screen prefetch batch,
	// defeating the point of prioritizing it in the shared queue at all.
	if (priority !== 'high') {
		await delay(DELAY);
	}
	try {
		await downloadToCache(
			CACHE_IMAGES_NAME,
			payload.images,
			undefined,
			priority,
			ownerResolver(payload.IssueID, payload.imageOwners),
		);
		info(`Success downloading the images`);
	} catch (e) {
		error(e);
	}
}

async function onDownloadImages(
	payload: DownloadImagesPayload,
	event: ExtendableMessageEvent,
): Promise<void> {
	const { images, IssueID } = payload;
	info(`Download images in the service worker`);
	await delay(2000);
	try {
		const clients = await self.clients.matchAll({ type: 'window' });
		info(`I start downloading the ${images.length} images`);
		// Explicit, port-based download request -- the caller is waiting on
		// a direct reply, so treat it as high priority.
		await downloadToCache(
			CACHE_IMAGES_NAME,
			images,
			(completed, total, image) => {
				for (const client of clients) {
					client.postMessage({
						type: 'DOWNLOAD_IMAGE',
						payload: {
							image,
							cacheName: CACHE_IMAGES_NAME,
							completed,
							total,
						},
					});
				}
			},
			'high',
			ownerResolver(IssueID),
		);
		info(
			`I finish processing all the images (${images.length}) for the issue ${IssueID}`,
		);
		event.ports[0].postMessage({
			type: 'DOWNLOAD_IMAGES',
			payload,
		});
		for (const client of clients) {
			client.postMessage({
				type: 'DOWNLOAD_IMAGES',
				payload,
			});
		}
	} catch (e) {
		event.ports[0].postMessage({
			type: 'ERROR',
			payload: {
				error: e,
			},
		});
	}
}

async function onDownloadVideos(payload: DownloadVideosPayload): Promise<void> {
	const { videos, IssueID } = payload;
	info(`Download videos in the service worker`);
	await delay(2000);
	try {
		info(`I start downloading the ${videos.length} videos`);
		await downloadToCache(
			CACHE_VIDEOS_NAME,
			videos,
			undefined,
			undefined,
			ownerResolver(IssueID),
		);
		info(
			`I finish processing all the videos (${videos.length}) for the issue ${IssueID}`,
		);
	} catch (e) {
		error(e);
	}
}

// this is necessary, since the new service worker will keep on skipWaiting state
// and then, caches will not be cleared since it is not activated
if (self.skipWaiting) {
	self.skipWaiting();
}

clientsClaim();

function swVersion() {
	info(`SW - Version: ${SW_VERSION}`);
	info(`SW - Package Version: ${PACKAGE_VERSION}`);
	info(`SW - Version Hash: ${VERSION_HASH}`);
}

async function handleVersionUpdate() {
	try {
		// Get stored version info
		const versionCache = await caches.open(VERSION_CACHE_NAME);
		const versionResponse = await versionCache.match('version-info');

		let previousVersion = null;
		if (versionResponse) {
			const versionData = await versionResponse.json();
			previousVersion = versionData.version;
		}

		// Store current version
		await versionCache.put(
			'version-info',
			new Response(
				JSON.stringify({
					version: SW_VERSION,
					hash: VERSION_HASH,
					packageVersion: PACKAGE_VERSION,
					timestamp: Date.now(),
				}),
			),
		);

		if (previousVersion && previousVersion !== SW_VERSION) {
			info(`Version changed from ${previousVersion} to ${SW_VERSION}`);
		}
	} catch (e) {
		error('Error handling version update:', e);
	}
}
